import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FilterMatchMode } from 'primereact/api';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Tag } from 'primereact/tag';
import { Calendar } from 'primereact/calendar';
import { Paginator } from 'primereact/paginator';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { ScrollTop } from 'primereact/scrolltop';
import { ProgressBar } from 'primereact/progressbar';
import { useAuth } from '../contexts/AuthContext.js';
import useStatusPageScript from '../hooks/useStatusPageScript.js';
import Login from './Login.js';
import supportAuditApi from '../api/supportAuditApi.js';
import '../config.js';

const SCOPE_LOCATION = 'location';
const SCOPE_CLIENT = 'client';
const SCOPE_ACCESS_POINT = 'accesspoint';
const SCOPE_HUB_RELAY = 'hubrelay';

const SCOPE_OPTIONS = [
  { label: 'Location', value: SCOPE_LOCATION },
  { label: 'Client', value: SCOPE_CLIENT },
  { label: 'Access Point', value: SCOPE_ACCESS_POINT },
  { label: 'Hub + Relay', value: SCOPE_HUB_RELAY },
];

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200].map((value) => ({ label: String(value), value }));

const HTTP_METHOD_OPTIONS = ['', 'GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((value) =>
  value
    ? { label: value, value }
    : { label: 'All methods', value: '' }
);

const safeParseJson = (input) => {
  if (input === null || input === undefined) {
    return { parsed: null, isValidJson: false, raw: '' };
  }

  if (typeof input === 'object') {
    return { parsed: input, isValidJson: true, raw: JSON.stringify(input) };
  }

  const raw = String(input);
  try {
    const parsed = JSON.parse(raw);
    return { parsed, isValidJson: true, raw };
  } catch (error) {
    return { parsed: null, isValidJson: false, raw };
  }
};

const toCompactPreview = (value, maxLength = 72) => {
  if (value === null || value === undefined || value === '') return '-';
  const text = String(value);
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
};

const formatCreated = (value) => {
  if (!value) return '-';
  let date = new Date(value);
  if (Number.isNaN(date.getTime()) && typeof value === 'string') {
    date = new Date(`${value}Z`);
  }
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return date.toLocaleString('en-GB');
};

const toStartOfDayIso = (value) => {
  if (!value) return null;
  const dt = new Date(value);
  const normalized = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate(), 0, 0, 0, 0);
  return normalized.toISOString();
};

const toEndOfDayIso = (value) => {
  if (!value) return null;
  const dt = new Date(value);
  const normalized = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate(), 23, 59, 59, 999);
  return normalized.toISOString();
};

const getChangedKeys = (oldParsed, newParsed) => {
  if (!oldParsed || !newParsed) return [];
  if (typeof oldParsed !== 'object' || typeof newParsed !== 'object') return [];
  if (Array.isArray(oldParsed) || Array.isArray(newParsed)) return [];

  const keySet = new Set([...Object.keys(oldParsed), ...Object.keys(newParsed)]);
  const changed = [];

  keySet.forEach((key) => {
    const oldValue = oldParsed[key];
    const newValue = newParsed[key];
    if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
      changed.push(key);
    }
  });

  return changed;
};

const getOperationSeverity = (operation) => {
  switch (operation) {
    case 'Added':
      return 'success';
    case 'Modified':
      return 'warning';
    case 'Deleted':
      return 'danger';
    default:
      return 'info';
  }
};

const SupportAuditLogs = () => {
  useStatusPageScript();

  const navigate = useNavigate();
  const { logout } = useAuth();

  const toast = useRef(null);

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(100);
  const [totalRecords, setTotalRecords] = useState(0);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [infoMessage, setInfoMessage] = useState('');
  const [isFallbackMode, setIsFallbackMode] = useState(false);
  const [fallbackAllEvents, setFallbackAllEvents] = useState([]);

  const [dateRange, setDateRange] = useState([null, null]);
  const [scope, setScope] = useState(SCOPE_LOCATION);
  const [locationId, setLocationId] = useState('');
  const [clientId, setClientId] = useState('');
  const [accessPointId, setAccessPointId] = useState('');
  const [hubId, setHubId] = useState('');
  const [relayId, setRelayId] = useState('');

  const [jsonDialogVisible, setJsonDialogVisible] = useState(false);
  const [jsonDialogTitle, setJsonDialogTitle] = useState('JSON details');
  const [jsonDialogRaw, setJsonDialogRaw] = useState('');
  const [jsonDialogPretty, setJsonDialogPretty] = useState('');
  const [jsonDialogChangedKeys, setJsonDialogChangedKeys] = useState([]);

  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    userEmail: { value: null, matchMode: FilterMatchMode.CONTAINS },
    httpMethod: { value: null, matchMode: FilterMatchMode.EQUALS },
    path: { value: null, matchMode: FilterMatchMode.CONTAINS },
    operation: { value: null, matchMode: FilterMatchMode.CONTAINS },
    entityName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    createdFormatted: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });
  const [globalFilterValue, setGlobalFilterValue] = useState('');
  const [httpMethodFilter, setHttpMethodFilter] = useState('');

  const progressbar = (
    <div className="card mt-3">
      <ProgressBar mode="indeterminate" style={{ height: '6px' }}></ProgressBar>
    </div>
  );

  const normalizedEvents = useMemo(() => {
    return (Array.isArray(events) ? events : []).map((item) => {
      const oldJson = safeParseJson(item.oldValues);
      const newJson = safeParseJson(item.newValues);
      const entityKeyJson = safeParseJson(item.entityKey);

      return {
        ...item,
        createdFormatted: formatCreated(item.created),
        operationText: item.operation || '-',
        entityKeyPreview: entityKeyJson.isValidJson
          ? toCompactPreview(JSON.stringify(entityKeyJson.parsed))
          : toCompactPreview(entityKeyJson.raw),
        oldValuesPreview: oldJson.isValidJson
          ? toCompactPreview(JSON.stringify(oldJson.parsed))
          : toCompactPreview(oldJson.raw),
        newValuesPreview: newJson.isValidJson
          ? toCompactPreview(JSON.stringify(newJson.parsed))
          : toCompactPreview(newJson.raw),
        changedKeys: item.operation === 'Modified' ? getChangedKeys(oldJson.parsed, newJson.parsed) : [],
      };
    });
  }, [events]);

  const buildQuery = (page, pageSize) => {
    const [fromDate, toDate] = Array.isArray(dateRange) ? dateRange : [null, null];
    const initDate = toStartOfDayIso(fromDate);
    const endDate = toEndOfDayIso(toDate || fromDate);

    return {
      initDate: initDate || undefined,
      endDate: endDate || undefined,
      page,
      pageSize,
    };
  };

  const hasScopeIds = () => {
    if (scope === SCOPE_LOCATION) {
      return Boolean(locationId.trim());
    }
    if (scope === SCOPE_CLIENT) {
      return Boolean(clientId.trim());
    }
    if (scope === SCOPE_ACCESS_POINT) {
      return Boolean(accessPointId.trim());
    }
    return Boolean(hubId.trim() && relayId.trim());
  };

  const calculateTotalRecords = (page, pageSize, itemCount) => {
    if (itemCount >= pageSize) {
      return (page + 1) * pageSize + 1;
    }
    return page * pageSize + itemCount;
  };

  const fetchScopedLogs = async (query) => {
    const appid = localStorage.getItem('appid');

    if (scope === SCOPE_LOCATION) {
      return supportAuditApi.getSupportAuditByLocation(Number(locationId), query, appid);
    }
    if (scope === SCOPE_CLIENT) {
      return supportAuditApi.getSupportAuditByClient(Number(clientId), query, appid);
    }
    if (scope === SCOPE_ACCESS_POINT) {
      return supportAuditApi.getSupportAuditByAccessPoint(Number(accessPointId), query, appid);
    }
    return supportAuditApi.getSupportAuditByHubRelay(hubId.trim(), relayId.trim(), query, appid);
  };

  const paginateFallbackRows = (items, page, pageSize) => {
    const start = page * pageSize;
    const end = start + pageSize;
    return items.slice(start, end);
  };

  const fetchAuditLogsWithFallback = async (query, page, pageSize) => {
    const appid = localStorage.getItem('appid');
    if (hasScopeIds()) {
      const scopedRows = await fetchScopedLogs(query);
      return {
        rows: Array.isArray(scopedRows) ? scopedRows : [],
        usedFallback: false,
        info: '',
      };
    }

    const fallbackRows = await supportAuditApi.getSupportAuditByDateRange(
      {
        initDate: query.initDate,
        endDate: query.endDate,
      },
      appid,
    );

    const safeRows = Array.isArray(fallbackRows) ? fallbackRows : [];
    return {
      rows: paginateFallbackRows(safeRows, page, pageSize),
      allRows: safeRows,
      usedFallback: true,
      info: 'No scope ID provided. Showing all changes in selected date range.',
    };
  };

  const fetchEvents = async (page, pageSize) => {
    try {
      setLoading(true);
      setErrorMessage('');
      setInfoMessage('');
      const query = buildQuery(page, pageSize);
      const result = await fetchAuditLogsWithFallback(query, page, pageSize);

      if (result.usedFallback) {
        const allRows = Array.isArray(result.allRows) ? result.allRows : [];
        setIsFallbackMode(true);
        setFallbackAllEvents(allRows);
        setInfoMessage(result.info);
        setEvents(Array.isArray(result.rows) ? result.rows : []);
        setTotalRecords(allRows.length);
      } else {
        const scopedRows = Array.isArray(result.rows) ? result.rows : [];
        setIsFallbackMode(false);
        setFallbackAllEvents([]);
        setEvents(scopedRows);
        setTotalRecords(calculateTotalRecords(page, pageSize, scopedRows.length));
      }
    } catch (error) {
      console.error('Error fetching support audit logs:', error);
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        setShowLoginModal(true);
      }
      setEvents([]);
      setFallbackAllEvents([]);
      setIsFallbackMode(false);
      setTotalRecords(0);
      setErrorMessage('Failed to load audit logs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      await fetchEvents(0, rows);
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLoginSuccess = () => {
    setShowLoginModal(false);
    if (toast.current) {
      toast.current.show({ severity: 'success', summary: '', detail: 'Login successful! You can continue working.' });
    }
    fetchEvents(Math.floor(first / rows), rows);
  };

  const onPageChange = (event) => {
    const nextRows = event.rows;
    const nextPage = event.page;
    setRows(nextRows);
    setFirst(event.first);
    if (isFallbackMode) {
      const paged = paginateFallbackRows(fallbackAllEvents, nextPage, nextRows);
      setEvents(paged);
      return;
    }
    fetchEvents(nextPage, nextRows);
  };

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    const nextFilters = { ...filters };
    nextFilters.global.value = value;
    setFilters(nextFilters);
    setGlobalFilterValue(value);
  };

  const applyFilters = () => {
    setFirst(0);
    fetchEvents(0, rows);
  };

  const clearFilters = () => {
    setDateRange([null, null]);
    setScope(SCOPE_LOCATION);
    setLocationId('');
    setClientId('');
    setAccessPointId('');
    setHubId('');
    setRelayId('');
    setGlobalFilterValue('');
    setHttpMethodFilter('');
    setInfoMessage('');
    setErrorMessage('');
    setIsFallbackMode(false);
    setFallbackAllEvents([]);
    setEvents([]);
    setRows(100);
    setTotalRecords(0);
    setFilters((prev) => ({
      ...prev,
      global: { ...prev.global, value: null },
      httpMethod: { ...prev.httpMethod, value: null, matchMode: FilterMatchMode.EQUALS },
    }));
    setFirst(0);
  };

  const logoutUser = async () => {
    await logout();
    navigate('/login');
  };

  const openJsonDialog = (title, rawValue, changedKeys = []) => {
    const parsed = safeParseJson(rawValue);
    setJsonDialogTitle(title);
    setJsonDialogRaw(parsed.raw || '-');
    setJsonDialogPretty(parsed.isValidJson ? JSON.stringify(parsed.parsed, null, 2) : parsed.raw || '-');
    setJsonDialogChangedKeys(changedKeys);
    setJsonDialogVisible(true);
  };

  const operationBodyTemplate = (rowData) => {
    return <Tag value={rowData.operationText} severity={getOperationSeverity(rowData.operation)}></Tag>;
  };

  const createdBodyTemplate = (rowData) => {
    return rowData.createdFormatted;
  };

  const jsonBodyTemplate = (field, title) => (rowData) => {
    const previewField = `${field}Preview`;
    const previewText = rowData[previewField] || '-';
    const changedKeys = rowData.operation === 'Modified' && (field === 'oldValues' || field === 'newValues')
      ? rowData.changedKeys
      : [];

    return (
      <div className="flex align-items-center gap-2">
        <span title={previewText}>{previewText}</span>
        <Button
          type="button"
          icon="pi pi-eye"
          className="p-button-text p-button-sm"
          onClick={() => openJsonDialog(title, rowData[field], changedKeys)}
          tooltip="View JSON"
          tooltipOptions={{ position: 'bottom' }}
        />
      </div>
    );
  };

  const changedKeysTemplate = (rowData) => {
    if (!rowData.changedKeys || rowData.changedKeys.length === 0) {
      return null;
    }

    const shown = rowData.changedKeys.slice(0, 2);
    const remaining = rowData.changedKeys.length - shown.length;

    return (
      <div className="flex align-items-center gap-1 mt-1">
        {shown.map((key) => (
          <Tag key={`${rowData.id}-${key}`} value={key} severity="warning"></Tag>
        ))}
        {remaining > 0 && <Tag value={`+${remaining}`} severity="warning"></Tag>}
      </div>
    );
  };

  return (
    <Container fluid="md" className="justify-content-md-center mt-5">
        <div className="panel">
          <div className="flex align-content-center flex-wrap">
            <Button link icon="pi pi-arrow-left" onClick={() => navigate(-1)} className="flex mb-3" />
            <h4 className="flex mt-2 mb-3">Support Audit Logs</h4>

            <span className="p-input-icon-left mb-3 ml-3">
              <i className="pi pi-search" />
              <InputText
                value={globalFilterValue}
                onChange={onGlobalFilterChange}
                placeholder="Filter Search"
                tooltip="Start typing to filter"
                tooltipOptions={{ position: 'bottom' }}
              />
            </span>

            <div className="card flex justify-content-center ml-3 mb-3" style={{ minWidth: '12rem' }}>
              <Dropdown
                value={httpMethodFilter}
                options={HTTP_METHOD_OPTIONS}
                onChange={(e) => {
                  const value = String(e.value || '');
                  setHttpMethodFilter(value);
                  setFilters((prev) => ({
                    ...prev,
                    httpMethod: {
                      ...prev.httpMethod,
                      matchMode: FilterMatchMode.EQUALS,
                      value: value ? value : null,
                    },
                  }));
                }}
                placeholder="HttpMethod"
                showClear={false}
              />
            </div>

            <div className="card flex justify-content-center ml-3 mb-3">
              <Calendar
                value={dateRange[0]}
                onChange={(e) => setDateRange([e.value, dateRange[1]])}
                dateFormat="dd/mm/yy"
                showIcon
                showButtonBar
                placeholder="From"
                readOnlyInput
              />
            </div>

            <div className="card flex justify-content-center ml-3 mb-3">
              <Calendar
                value={dateRange[1]}
                onChange={(e) => setDateRange([dateRange[0], e.value])}
                dateFormat="dd/mm/yy"
                showIcon
                showButtonBar
                placeholder="To"
                readOnlyInput
              />
            </div>

            <div
              className="flex align-items-center ml-3 mb-3"
              style={{
                flexWrap: 'wrap',
                gap: '0.5rem',
                width: '100%',
                maxWidth: '34rem',
              }}
            >
              <div className="card flex justify-content-center" style={{ minWidth: '12rem', width: '100%', maxWidth: '13rem' }}>
                <Dropdown
                  value={scope}
                  options={SCOPE_OPTIONS}
                  onChange={(e) => setScope(e.value)}
                  placeholder="Select scope"
                />
              </div>

              {scope === SCOPE_LOCATION && (
                <span className="p-input-icon-left" style={{ width: '100%', maxWidth: '13rem' }}>
                  <i className="pi pi-map-marker" />
                  <InputText
                    value={locationId}
                    onChange={(e) => setLocationId(e.target.value)}
                    keyfilter="int"
                    placeholder="Location ID"
                    tooltip="Enter location id"
                    tooltipOptions={{ position: 'bottom' }}
                    style={{ width: '100%' }}
                  />
                </span>
              )}

              {scope === SCOPE_CLIENT && (
                <span className="p-input-icon-left" style={{ width: '100%', maxWidth: '13rem' }}>
                  <i className="pi pi-users" />
                  <InputText
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                    keyfilter="int"
                    placeholder="Client ID"
                    tooltip="Enter client id"
                    tooltipOptions={{ position: 'bottom' }}
                    style={{ width: '100%' }}
                  />
                </span>
              )}

              {scope === SCOPE_ACCESS_POINT && (
                <span className="p-input-icon-left" style={{ width: '100%', maxWidth: '13rem' }}>
                  <i className="pi pi-sitemap" />
                  <InputText
                    value={accessPointId}
                    onChange={(e) => setAccessPointId(e.target.value)}
                    keyfilter="int"
                    placeholder="Access Point ID"
                    tooltip="Enter access point id"
                    tooltipOptions={{ position: 'bottom' }}
                    style={{ width: '100%' }}
                  />
                </span>
              )}

              {scope === SCOPE_HUB_RELAY && (
                <>
                  <span className="p-input-icon-left" style={{ width: '100%', maxWidth: '13rem' }}>
                    <i className="pi pi-server" />
                    <InputText
                      value={hubId}
                      onChange={(e) => setHubId(e.target.value)}
                      placeholder="Hub ID"
                      tooltip="Enter hub id"
                      tooltipOptions={{ position: 'bottom' }}
                      style={{ width: '100%' }}
                    />
                  </span>

                  <span className="p-input-icon-left" style={{ width: '100%', maxWidth: '13rem' }}>
                    <i className="pi pi-link" />
                    <InputText
                      value={relayId}
                      onChange={(e) => setRelayId(e.target.value)}
                      placeholder="Relay ID"
                      tooltip="Enter relay id"
                      tooltipOptions={{ position: 'bottom' }}
                      style={{ width: '100%' }}
                    />
                  </span>
                </>
              )}
            </div>

            <div className="card flex justify-content-center ml-3 mb-3" style={{ minWidth: '8rem' }}>
              <Dropdown
                value={rows}
                options={PAGE_SIZE_OPTIONS}
                onChange={(e) => {
                  const nextRows = Number(e.value);
                  setRows(nextRows);
                  setFirst(0);
                }}
                placeholder="Page size"
              />
            </div>

            <div className="card flex justify-content-center ml-3 mb-3">
              <Button label="Search" className="p-button-raised" onClick={applyFilters} disabled={loading} />
            </div>

            <div className="card flex justify-content-center ml-3 mb-3">
              <Button label="Clear" className="p-button-outlined" onClick={clearFilters} disabled={loading} />
            </div>
          </div>
        </div>

        {infoMessage && (
          <div className="ml-3 mb-3">
            <small style={{ color: '#6c757d' }}>{infoMessage}</small>
          </div>
        )}

        {loading && progressbar}
        <Toast ref={toast} />

        {errorMessage && (
          <Row className="justify-content-md-center mt-3">
            <Col>
              <Card>
                <div className="flex align-items-center justify-content-between">
                  <span>{errorMessage}</span>
                  <Button label="Retry" icon="pi pi-refresh" className="p-button-sm" onClick={() => fetchEvents(Math.floor(first / rows), rows)} />
                </div>
              </Card>
            </Col>
          </Row>
        )}

        <Row className="justify-content-md-center mt-5">
          <Col>
            <DataTable
              value={normalizedEvents}
              dataKey="id"
              tableStyle={{ minWidth: '50rem' }}
              filters={filters}
              filterDisplay="menu"
              stripedRows
              globalFilterFields={[
                'createdFormatted',
                'userEmail',
                'httpMethod',
                'path',
                'operation',
                'entityName',
              ]}
              emptyMessage={loading ? 'Loading audit logs...' : 'No audit logs found for selected filters.'}
              scrollable
              scrollHeight="60vh"
            >
              <Column field="createdFormatted" header="Created" body={createdBodyTemplate} style={{ minWidth: '11rem' }}></Column>
              <Column field="userEmail" header="UserEmail" style={{ minWidth: '15rem' }}></Column>
              <Column field="httpMethod" header="HttpMethod" style={{ minWidth: '8rem' }}></Column>
              <Column field="path" header="Path" style={{ minWidth: '16rem' }}></Column>
              <Column field="operation" header="Operation" body={operationBodyTemplate} style={{ minWidth: '9rem' }}></Column>
              <Column field="entityName" header="EntityName" style={{ minWidth: '12rem' }}></Column>
              <Column field="entityKey" header="EntityKey" body={jsonBodyTemplate('entityKey', 'EntityKey')}></Column>
              <Column field="oldValues" header="OldValues" body={jsonBodyTemplate('oldValues', 'OldValues')}></Column>
              <Column
                field="newValues"
                header="NewValues"
                body={(rowData) => (
                  <div>
                    {jsonBodyTemplate('newValues', 'NewValues')(rowData)}
                    {changedKeysTemplate(rowData)}
                  </div>
                )}
                style={{ minWidth: '16rem' }}
              ></Column>
            </DataTable>
          </Col>
        </Row>

        <Row className="justify-content-md-center mt-5">
          <div className="card">
            <Paginator
              first={first}
              rows={rows}
              totalRecords={totalRecords}
              rowsPerPageOptions={[25, 50, 100, 200]}
              onPageChange={onPageChange}
            />
          </div>
        </Row>

        <Dialog
          header={jsonDialogTitle}
          visible={jsonDialogVisible}
          style={{ width: '60vw' }}
          modal={true}
          onHide={() => setJsonDialogVisible(false)}
        >
          {jsonDialogChangedKeys.length > 0 && (
            <div className="mb-3">
              <h6 className="mb-2">Changed fields</h6>
              <div className="flex align-items-center gap-2 flex-wrap">
                {jsonDialogChangedKeys.map((key) => (
                  <Tag key={`dialog-${key}`} value={key} severity="warning"></Tag>
                ))}
              </div>
            </div>
          )}

          <div className="mb-3">
            <h6 className="mb-2">Pretty JSON</h6>
            <pre style={{ maxHeight: '45vh', overflow: 'auto', whiteSpace: 'pre-wrap' }}>{jsonDialogPretty}</pre>
          </div>

          <div>
            <h6 className="mb-2">Raw value</h6>
            <pre style={{ maxHeight: '25vh', overflow: 'auto', whiteSpace: 'pre-wrap' }}>{jsonDialogRaw}</pre>
          </div>
        </Dialog>

        <div className="card flex justify-content-center">
          <Dialog
            header="Session Expired - Please Login Again"
            visible={showLoginModal}
            style={{ width: '450px' }}
            modal={true}
            closable={false}
            className="justify-content-center"
          >
            <p className="mb-3">Your session has expired. Please log in again to continue working without losing your form data.</p>
            <div className="flex justify-content-center">
              <Login onLoginSuccess={handleLoginSuccess} />
            </div>
          </Dialog>
        </div>
      <ScrollTop />
    </Container>
  );
};

export default SupportAuditLogs;
